'use client';

import useSWR from 'swr';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { fetchWithTracingAndLogging } from '@/app/components/hotel-detail/common/fetchWithTracing';

type HotelRankingResponse = {
  hotelOpinion?: { opinionId?: string };
};

/**
 * Lazily fetches the TrustYou hotel ID (opinionId) for a given internal hotel ID
 * via the staticdb hotels_ranking endpoint. Only makes the network request when
 * `enabled` is true (i.e. when the ratings popover/modal is open).
 */
export const useHotelRanking = (hotelId: number | undefined) => {
  const { localization, searchInfoData, commonData } = usePageWrapperData();
  const { businessProfile } = localization;

  const loggingInfo: LoggingInfo = {
    tags: {
      page: searchInfoData.searchMode.toLowerCase(),
      service: 'hotels-ranking',
      module: 'useHotelRanking',
      debug: false,
    },
  };

  // Composite string key (not a URL) because the actual request is a POST.
  const key = hotelId ? `hotels-ranking/${hotelId}-${businessProfile}-${searchInfoData.searchMode}` : null;

  const { data, isLoading, error } = useSWR<HotelRankingResponse>(
    key,
    () =>
      fetchWithTracingAndLogging<HotelRankingResponse>(
        `${BASE_PATH}/api/search/hotelsRanking`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            internalHotelId: hotelId,
            searchType: searchInfoData.searchMode,
            businessProfileId: businessProfile,
          }),
        },
        loggingInfo,
        searchInfoData.searchTraceParent,
        commonData
      ),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: true,
      shouldRetryOnError: false,
      dedupingInterval: 1000 * 60 * 30,
    }
  );

  return {
    trustYouHotelId: data?.hotelOpinion?.opinionId,
    isLoading,
    isError: !!error,
  };
};
